import { Box, useTheme } from "@mui/material";
import { eq, type InitialQueryBuilder } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import React, { useContext, useEffect, useRef, useState } from "react";

import { useRegisterSW } from "virtual:pwa-register/react";
import { useAutoApplyUpdate } from "@/hooks/useAutoApplyUpdate.ts";
import UseTimeouts from "@/hooks/useTimeouts.ts";
import { forceReload } from "@/models/BootstrapUtils.ts";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";
import { store } from "@/store/store";
import CornerStack from "@/ui/CornerStack";

const hasFeedsQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ feed: store.collection("feed") })
    .where(({ feed }) => eq(feed.workspaceId, workspaceId))
    .orderBy(({ feed }) => feed.id)
    .limit(1)
    .select(({ feed }) => ({ id: feed.id }));

export default function ReloadPrompt() {
  const theme = useTheme();
  const { currentWorkspaceId } = useContext(WorkspaceContext);

  const { data: feedRows } = useLiveQuery((q) => (currentWorkspaceId ? hasFeedsQuery(currentWorkspaceId)(q) : null));
  const feedsExist = (feedRows?.length ?? 0) > 0;

  const [counter, setCounter] = useState<number>(0);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [showReloadApplication, setShowReloadApplication] = useState<boolean>(false);
  const { refresh } = UseTimeouts(10);

  const reloadApplication = () => {
    setDisabled(true);
    forceReload()
      .then(() => {
        setDisabled(false);
        window.location.reload();
      })
      .catch((e) => {
        setDisabled(false);
        console.log({ e });
        window.location.reload();
      });
  };

  const prevWorkspaceIdRef = useRef(currentWorkspaceId);

  useEffect(() => {
    if (!currentWorkspaceId) {
      return;
    }

    // When the workspace changes, reset and bail out so the rest of the logic
    // never acts on stale snapshot values from the previous workspace.
    if (prevWorkspaceIdRef.current !== currentWorkspaceId) {
      prevWorkspaceIdRef.current = currentWorkspaceId;
      setShowReloadApplication(false);
      setCounter(0);
      return;
    }

    if (feedsExist && showReloadApplication) {
      setShowReloadApplication(false);
      return;
    }
    if (refresh === true && !feedsExist && counter >= 1 && !showReloadApplication) {
      setShowReloadApplication(true);
    }
    if (refresh) {
      setCounter((value) => value + 1);
    }
  }, [refresh, feedsExist, counter, showReloadApplication, currentWorkspaceId]);

  const updatePollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined);
  useEffect(() => () => clearInterval(updatePollRef.current), []);

  const registerPeriodicSync = (period: number, swUrl: string, r: ServiceWorkerRegistration) => {
    if (period <= 0) return;

    clearInterval(updatePollRef.current);
    updatePollRef.current = setInterval(async () => {
      if ("onLine" in navigator && !navigator.onLine) {
        return;
      }

      try {
        const resp = await fetch(swUrl, {
          cache: "no-store",
          headers: {
            cache: "no-store",
            "cache-control": "no-cache",
          },
        });

        if (resp?.status === 200) {
          await r.update();
        } else {
          console.log(`%cService Worker Status %c[${resp?.status}]`, "color:#666;", "color:red;");
        }
      } catch (e) {
        console.log(`ERROR LOADING ${swUrl}`);
      }
    }, period);
  };
  const period = 1000 * 30; // check every 30 seconds
  const serviceWorker = {
    onRegisteredSW(swUrl, r) {
      // Start the poll unconditionally: gating on active/installing state left clients whose
      // worker was in any other state at registration time without update polling forever, so
      // they silently pinned to months-old bundles (WEB-2142). r.update() is safe in any state.
      if (!r) return;
      registrationRef.current = r;
      // The browser already checks for a new worker on registration; this second check is a cheap backstop so a cold
      // boot finds a waiting build within its boot window even where that check was skipped or served stale (WEB-2417).
      r.update().catch((e) => console.log(`ERROR CHECKING ${swUrl}`, e));
      registerPeriodicSync(period, swUrl, r);
    },
  };
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW(serviceWorker);

  const updateAndReload = () => {
    updateServiceWorker();
    setTimeout(() => {
      window.location.href = "/";
    }, 250);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  useAutoApplyUpdate(needRefresh, registrationRef, updateAndReload);

  const boxProps = {
    display: "flex",
    gap: 1,
    alignItems: "center",
    fontSize: "14px",
    border: `solid 1px ${theme.palette.secondary.light}`,
    background: theme.palette.secondary.main,
    py: 0.5,
    px: 1,
    borderRadius: 1,
  };

  return (
    <Box role="alert" aria-labelledby="toast-message">
      {showReloadApplication && (
        <CornerStack order={0}>
          <Box sx={{ ...boxProps }}>
            <button
              disabled={disabled}
              style={{ padding: "0.25rem  0.5rem" }}
              onClick={reloadApplication}
              className="basic-button grays"
            >
              Click To Clear Cache & Reload
            </button>
          </Box>
        </CornerStack>
      )}
      {needRefresh && (
        <CornerStack order={0}>
          <Box sx={{ ...boxProps }}>
            <Box>Your version is out of date.</Box>
            <Box>
              <Box
                component="button"
                sx={{
                  fontSize: "13px",
                  border: "solid 1px #a92afe",
                  background: "#8804ff",
                  "&:hover": { background: "#6700c3" },
                }}
                className="basic-button "
                onClick={() => updateAndReload()}
              >
                Update
              </Box>
            </Box>
          </Box>
        </CornerStack>
      )}
    </Box>
  );
}
